import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Typography, Chip, IconButton, Tooltip } from '@mui/material';
import { Visibility as VisibilityIcon, Edit as EditIcon } from '@mui/icons-material';
import type { SavedQuery } from '../../../../hooks/QueryBuilder/useSavedQueries';
import { QuerySource } from '../../../../types/QueryBuilder';
import { toQuerySource } from '../../../../utils/queryBuilderUtils';
import { useLanguage } from '../../../../context/LanguageContext';

interface SavedQueriesTableProps {
    queries: SavedQuery[];
    onView: (query: SavedQuery) => void;
    onEdit: (query: SavedQuery) => void;
    onTypeFilter: (isFromCompany: 0 | 1) => void;
    onSourceFilter: (source: QuerySource) => void;
}

const COLUMN_WIDTHS = ['35%', '15%', '15%', '15%', '10%', '10%'];

const SOURCE_CHIP_SX: Record<QuerySource, object> = {
    ADVANCED: { bgcolor: 'badge.orange.bg', color: 'badge.orange.fg', cursor: 'pointer' },
    SIMPLE: { bgcolor: 'badge.green.bg', color: 'badge.green.fg', cursor: 'pointer' },
};

function SavedQueriesTable({ queries, onView, onEdit, onTypeFilter, onSourceFilter }: SavedQueriesTableProps) {
    const { t, formatLocale } = useLanguage();
    const headers = [
        t('pages.clients.queryBuilder.description'),
        t('pages.clients.queryBuilder.group'),
        t('pages.clients.queryBuilder.date'),
        t('pages.clients.queryBuilder.type'),
        t('pages.clients.queryBuilder.myQueriesTab.builderColumn'),
    ];

    return (
        <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
            <Table sx={{ tableLayout: 'fixed', width: '100%' }}>
                <colgroup>
                    {COLUMN_WIDTHS.map((width, index) => <col key={index} style={{ width }} />)}
                </colgroup>
                <TableHead>
                    <TableRow sx={{ bgcolor: 'grey.50' }}>
                        {headers.map(header => <TableCell key={header} sx={{ fontWeight: 600 }}>{header}</TableCell>)}
                        <TableCell sx={{ fontWeight: 600 }} align="center">{t('pages.clients.queryBuilder.actions')}</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {queries.map(query => {
                        const name = query.DESCRIPTION || query.QUERY_NAME;
                        const date = new Date(query.APPLICATION_DATE);
                        const isFromCompany = query.IS_FROM_COMPANY === '1';
                        const source = toQuerySource(query.SOURCE);

                        return (
                            <TableRow key={query.QUERY_ID} hover>
                                <TableCell sx={{ overflow: 'hidden' }}>
                                    <Typography variant="body2" noWrap title={name || ''}>{name || '-'}</Typography>
                                </TableCell>
                                <TableCell>
                                    {query.QUERY_GROUP_NAME && (
                                        <Chip label={query.QUERY_GROUP_NAME} size="small" sx={{ bgcolor: 'badge.blue.bg', color: 'badge.blue.fg' }} />
                                    )}
                                </TableCell>
                                <TableCell>
                                    <Typography variant="body2">
                                        {date.toLocaleDateString(formatLocale, { day: '2-digit', month: '2-digit', year: 'numeric' })}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {date.toLocaleTimeString(formatLocale, { hour: '2-digit', minute: '2-digit' })}
                                    </Typography>
                                </TableCell>
                                <TableCell>
                                    <Chip
                                        label={t(isFromCompany ? 'pages.clients.queryBuilder.brand' : 'pages.clients.queryBuilder.personal')}
                                        size="small"
                                        color="primary"
                                        onClick={() => onTypeFilter(isFromCompany ? 1 : 0)}
                                        sx={{ cursor: 'pointer' }}
                                    />
                                </TableCell>
                                <TableCell>
                                    <Chip
                                        label={t(source === 'ADVANCED' ? 'pages.clients.queryBuilder.myQueriesTab.advanced' : 'pages.clients.queryBuilder.myQueriesTab.simple')}
                                        size="small"
                                        onClick={() => onSourceFilter(source)}
                                        sx={SOURCE_CHIP_SX[source]}
                                    />
                                </TableCell>
                                <TableCell align="center">
                                    <Tooltip title={t('pages.clients.queryBuilder.view')}>
                                        <IconButton size="small" color="primary" onClick={() => onView(query)}>
                                            <VisibilityIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title={t('pages.clients.queryBuilder.modify')}>
                                        <IconButton size="small" color="secondary" onClick={() => onEdit(query)}>
                                            <EditIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </TableCell>
                            </TableRow>
                        );
                    })}
                </TableBody>
            </Table>
        </TableContainer>
    );
}

export default SavedQueriesTable;
